// /reports/purchase-register — Purchase Register (QA PU4-010, docs/purchase-gaps-plan.md §5.8)
//
// Posted supplier bills (and debit notes, as negative rows) by bill date. Each
// row is in its document's currency; the KPIs and the base column are in the
// tenant currency. India shows CGST / SGST / IGST, a VAT tenant one VAT column.

import { useState } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { Badge } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  KpiIcons,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
} from '@/features/reports';
import type { PurchaseRegisterRow, ReportKpiTile } from '@/features/reports';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';

const SUPPLY_LABEL: Record<PurchaseRegisterRow['supply_type'], string> = {
  INTRA: 'Intra-state',
  INTER: 'Inter-state',
  IMPORT: 'Import',
  NONE: '',
};

/** Minor units → a plain CSV number in that currency's decimals. */
function csvAmount(minor: number, currency: CurrencyCode): string {
  const subunit = getSubunitMultiplier(currency);
  return (minor / subunit).toFixed(Math.round(Math.log10(subunit)));
}

export function PurchaseRegisterReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const tax = useTaxRegime();
  const isGst = tax.regime === 'GST_INDIA';

  const { data, isLoading, error } = useReportQuery({
    key: 'purchase-register',
    filter,
    queryFn: (f) => reportsApi.getPurchaseRegister(f),
  });

  const rows = data?.rows ?? [];
  const docCurrency = (r: PurchaseRegisterRow) => (r.currency_code ?? currency) as CurrencyCode;
  const hasForeign = rows.some((r) => r.currency_code && r.currency_code !== currency);

  const columns = ((): ColumnDef<PurchaseRegisterRow>[] => {
    const money = (get: (r: PurchaseRegisterRow) => number) => (_v: unknown, r: PurchaseRegisterRow) => (
      <span className={`tabular-nums ${get(r) < 0 ? 'text-danger-600 dark:text-danger-400' : ''}`}>{formatPaise(get(r), docCurrency(r))}</span>
    );
    const cols: ColumnDef<PurchaseRegisterRow>[] = [
      { key: 'date', header: 'Date', render: (_v, r) => <span className="tabular-nums text-sm">{r.date}</span> },
      {
        key: 'number',
        header: 'Document',
        render: (_v, r) => (
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{r.number}</span>
            {r.kind === 'DEBIT_NOTE' ? <Badge variant="danger" size="sm">Debit note</Badge> : <span className="text-xs text-neutral-500 dark:text-neutral-400">{r.po_number}</span>}
          </div>
        ),
      },
      {
        key: 'supplier_name',
        header: 'Supplier',
        render: (_v, r) => (
          <div className="flex flex-col">
            <span className="text-sm text-neutral-900 dark:text-neutral-100">{r.supplier_name ?? '—'}</span>
            {r.supplier_gstin ? <span className="text-xs font-mono text-neutral-500 dark:text-neutral-400">{r.supplier_gstin}</span> : null}
          </div>
        ),
      },
      { key: 'vendor_invoice_number', header: 'Supplier invoice', render: (_v, r) => <span className="text-sm">{r.vendor_invoice_number ?? '—'}{r.vendor_invoice_date ? <span className="block text-xs text-neutral-500 dark:text-neutral-400">{r.vendor_invoice_date}</span> : null}</span> },
      { key: 'location_name', header: 'Store', render: (_v, r) => <span className="text-sm">{r.location_name ?? '—'}</span> },
      { key: 'taxable', header: 'Taxable', numeric: true, render: money((r) => r.taxable) },
    ];
    if (isGst) {
      cols.push(
        { key: 'cgst', header: 'CGST', numeric: true, render: money((r) => r.cgst) },
        { key: 'sgst', header: 'SGST', numeric: true, render: money((r) => r.sgst) },
        { key: 'igst', header: 'IGST', numeric: true, render: money((r) => r.igst) },
      );
    } else {
      cols.push({ key: 'vat', header: tax.taxName, numeric: true, render: money((r) => r.vat) });
    }
    cols.push(
      { key: 'rates', header: 'Rates', render: (_v, r) => <span className="text-xs text-neutral-500 dark:text-neutral-400">{r.rates.map((x) => `${x}%`).join(', ') || '0%'}{isGst && SUPPLY_LABEL[r.supply_type] ? ` · ${SUPPLY_LABEL[r.supply_type]}` : ''}</span> },
      { key: 'total', header: 'Total', numeric: true, render: (_v, r) => <span className={`text-sm font-semibold tabular-nums ${r.total < 0 ? 'text-danger-600 dark:text-danger-400' : ''}`}>{formatPaise(r.total, docCurrency(r))}</span> },
    );
    if (hasForeign) cols.push({ key: 'base_total', header: `Total (${currency})`, numeric: true, render: (_v, r) => <span className="tabular-nums">{formatPaise(r.base_total, currency)}</span> });
    return cols;
  })();

  const kpis: ReportKpiTile[] = data
    ? [
        { id: 'bills', title: 'Bills', value: String(data.totals.bills), subLabel: data.totals.debit_notes ? `${data.totals.debit_notes} debit note(s)` : undefined, icon: KpiIcons.receipt, tone: 'brand' },
        { id: 'taxable', title: 'Taxable value', value: formatPaise(data.totals.taxable, currency), icon: KpiIcons.cart, tone: 'indigo' },
        { id: 'tax', title: `${tax.taxName} paid`, value: formatPaise(data.totals.tax, currency), icon: KpiIcons.percent, tone: 'teal' },
        { id: 'total', title: 'Total purchases', value: formatPaise(data.totals.total, currency), icon: KpiIcons.wallet, tone: 'violet' },
      ]
    : [];

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      window.print();
      return;
    }
    if (fmt === 'csv') {
      const taxCols = isGst
        ? [
            { header: 'CGST', get: (r: PurchaseRegisterRow) => csvAmount(r.cgst, docCurrency(r)) },
            { header: 'SGST', get: (r: PurchaseRegisterRow) => csvAmount(r.sgst, docCurrency(r)) },
            { header: 'IGST', get: (r: PurchaseRegisterRow) => csvAmount(r.igst, docCurrency(r)) },
          ]
        : [{ header: tax.taxName, get: (r: PurchaseRegisterRow) => csvAmount(r.vat, docCurrency(r)) }];
      downloadCsv(
        `purchase-register-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Date', get: (r: PurchaseRegisterRow) => r.date },
          { header: 'Type', get: (r: PurchaseRegisterRow) => (r.kind === 'DEBIT_NOTE' ? 'Debit note' : 'Bill') },
          { header: 'Number', get: (r: PurchaseRegisterRow) => r.number },
          { header: 'PO', get: (r: PurchaseRegisterRow) => r.po_number ?? '' },
          { header: 'Supplier', get: (r: PurchaseRegisterRow) => r.supplier_name ?? '' },
          { header: tax.taxIdLabel ?? 'Tax ID', get: (r: PurchaseRegisterRow) => r.supplier_gstin ?? '' },
          { header: 'Supplier invoice', get: (r: PurchaseRegisterRow) => r.vendor_invoice_number ?? '' },
          { header: 'Invoice date', get: (r: PurchaseRegisterRow) => r.vendor_invoice_date ?? '' },
          { header: 'Store', get: (r: PurchaseRegisterRow) => r.location_name ?? '' },
          { header: 'Currency', get: (r: PurchaseRegisterRow) => docCurrency(r) },
          { header: 'Taxable', get: (r: PurchaseRegisterRow) => csvAmount(r.taxable, docCurrency(r)) },
          ...taxCols,
          { header: 'Rates', get: (r: PurchaseRegisterRow) => r.rates.map((x) => `${x}%`).join(' ') },
          { header: 'Total', get: (r: PurchaseRegisterRow) => csvAmount(r.total, docCurrency(r)) },
          { header: `Total (${currency})`, get: (r: PurchaseRegisterRow) => csvAmount(r.base_total, currency) },
        ],
        rows,
      );
      return;
    }
    shareReportLink(fmt, 'Orio360 Purchase Register', `${data?.totals.bills ?? 0} bills • ${formatPaise(data?.totals.total ?? 0, currency)}`);
  };

  return (
    <ReportPageShell
      title="Purchase Register"
      subtitle="Posted supplier bills and debit notes with taxable value and tax."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Purchase Register' }]}
      accent="indigo-violet"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow tiles={kpis} />
          <ReportTable title="Bills and debit notes" columns={columns} data={rows} emptyTitle="No posted supplier bills in this period" totalCount={rows.length} />
        </>
      )}
    </ReportPageShell>
  );
}
